// Individual message bubble component

import React, { memo, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {ChatMessage, FeedbackResponse, FeedbackType} from '../types/chat';
import ToolCallMessage from './ToolCallMessage';
import EntityHover from './EntityHover';
import ImageWithPlaceholder from './ImageWithPlaceholder';
import FeedbackDialog from './FeedbackDialog';
import SimilarityBadge from './SimilarityBadge';
import { authService } from '../services/authService';
import { chatApi } from '../services/chatApi';
import { conversationApi } from '../services/conversationApi';
import '../styles/ImagePlaceholder.css';

/**
 * Buffers markdown content to prevent incomplete elements from rendering.
 * Only renders complete images, table rows, and links.
 * Table rows are buffered until a newline appears after the closing pipe (|),
 * ensuring the entire row is rendered at once rather than cell by cell.
 *
 * @param content - The raw markdown content
 * @param isStreaming - Whether the message is currently streaming
 * @returns Buffered content with incomplete elements removed
 */
const bufferMarkdownElements = (content: string, isStreaming: boolean = false): string => {
  if (!isStreaming || !content) {
    return content;
  }

  let bufferedContent = content;

  // Buffer incomplete images: ![alt](url)
  // Only show images with complete closing parenthesis
  const incompleteImageMatch = bufferedContent.match(/!\[([^\]]*?)(\](\([^)]*)?)?$/);
  if (incompleteImageMatch && incompleteImageMatch.index !== undefined) {
    // Remove incomplete image syntax from the end
    bufferedContent = bufferedContent.slice(0, incompleteImageMatch.index);
  }

  // Buffer incomplete links: [text](url)
  // Only show links with complete closing parenthesis
  const incompleteLinkMatch = bufferedContent.match(/\[([^\]]*?)(\](\([^)]*)?)?$/);
  if (incompleteLinkMatch && incompleteLinkMatch.index !== undefined) {
    // Remove incomplete link syntax from the end
    bufferedContent = bufferedContent.slice(0, incompleteLinkMatch.index);
  }

  // Buffer incomplete table rows
  // A table row starts with | and should end with | followed by a newline
  // We want to render the whole row at once, only when we receive the newline after the closing |
  const lines = bufferedContent.split('\n');
  if (lines.length > 0) {
    const lastLine = lines[lines.length - 1];

    // If the last line is a table row (starts with |), we need to check if it's complete
    if (lastLine.trim().startsWith('|')) {
      // Check if the content originally ended with a newline after this row
      // If there's no newline after the last row, it means the row is still being streamed
      const endsWithNewline = bufferedContent.endsWith('\n');

      // If the last line is a table row but there's no newline after it yet,
      // buffer it until we receive the newline (which signals the row is complete)
      if (!endsWithNewline) {
        // Remove the incomplete row
        lines.pop();
        bufferedContent = lines.join('\n');
      }
    }
  }

  return bufferedContent;
};

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  onSuggestionClick?: (suggestion: string) => void;
  conversationId?: string;
  existingFeedback?: FeedbackResponse;
  onReloadConversation?: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = memo(({ message, isStreaming, onSuggestionClick, conversationId, existingFeedback, onReloadConversation }) => {
  // Feedback state - initialize from existing feedback if available
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(existingFeedback?.feedbackType || null);
  const [feedbackText, setFeedbackText] = useState<string | undefined>(existingFeedback?.feedbackText);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [pendingFeedbackType, setPendingFeedbackType] = useState<FeedbackType | null>(null);

  // Grace period polling state
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const isCheckingStatusRef = useRef(false);

  // Update feedback state when existingFeedback changes (e.g., on conversation load)
  useEffect(() => {
    if (existingFeedback) {
      setFeedbackType(existingFeedback.feedbackType);
      setFeedbackText(existingFeedback.feedbackText);
    }
  }, [existingFeedback]);

  // Poll for generation completion when system message (grace period) is shown
  useEffect(() => {
    if (message.type !== 'system' || !conversationId || !onReloadConversation) {
      return;
    }

    // eslint-disable-next-line prefer-const
    let intervalId: NodeJS.Timeout | undefined;
    // eslint-disable-next-line prefer-const
    let timeoutId: NodeJS.Timeout | undefined;
    // eslint-disable-next-line prefer-const
    let finalCheckTimeout: NodeJS.Timeout | undefined;
    let isMounted = true;
    let lastMessageSnapshot: string | null = null;

    const checkForNewMessages = async () => {
      if (!isMounted || isCheckingStatusRef.current) return;

      try {
        isCheckingStatusRef.current = true;
        const messages = await conversationApi.getConversationMessages(conversationId);

        // Create a snapshot of the last message (content + type)
        const currentSnapshot = messages.length > 0
          ? `${messages[messages.length - 1].type}:${messages[messages.length - 1].content}`
          : null;

        // On first check, just store the current snapshot
        if (lastMessageSnapshot === null) {
          lastMessageSnapshot = currentSnapshot;
          return;
        }

        // If last message changed (new message added OR content changed), reload!
        if (currentSnapshot !== lastMessageSnapshot) {
          setIsGenerationComplete(true);

          // Stop polling
          if (intervalId) {
            clearInterval(intervalId);
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (finalCheckTimeout) {
            clearTimeout(finalCheckTimeout);
          }

          // Auto-reload the conversation
          setTimeout(() => {
            if (isMounted) {
              onReloadConversation();
            }
          }, 200);
        }
      } catch (error) {
        console.error('Failed to check for new messages:', error);
      } finally {
        if (isMounted) {
          isCheckingStatusRef.current = false;
        }
      }
    };

    // Initial check after 2 seconds
    const initialTimeout = setTimeout(checkForNewMessages, 2000);

    // Poll every 3 seconds
    intervalId = setInterval(checkForNewMessages, 3000);

    // Do a final check right before stopping (grace period is 5 minutes, check at 302s)
    finalCheckTimeout = setTimeout(() => {
      checkForNewMessages();
    }, 302000);

    // Stop polling after grace period + buffer (300 seconds grace period + 30 seconds buffer)
    // This ensures we catch the timeout message if it's added
    timeoutId = setTimeout(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }, 330000);

    return () => {
      isMounted = false;
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (finalCheckTimeout) {
        clearTimeout(finalCheckTimeout);
      }
    };
  }, [message.type, conversationId, onReloadConversation]);

  // Download code block content
  const handleDownloadCode = useCallback((code: string, language: string) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `download.${language || 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Memoize markdown components to prevent re-creation on every render
  const markdownComponents = useMemo(() => ({
    // Custom paragraph renderer to prevent <pre> inside <p>
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement> & { children?: React.ReactNode }) => {
      // Check if any of the React children are <pre> elements
      // This is more reliable than checking the markdown AST
      const childrenArray = React.Children.toArray(children);
      const hasPreElement = childrenArray.some((child) => {
        return React.isValidElement(child) && child.type === 'pre';
      });

      // If paragraph contains <pre>, render as div to avoid nesting error
      if (hasPreElement) {
        return <div {...props}>{children}</div>;
      }

      // Otherwise render as normal paragraph (this includes paragraphs with inline code)
      return <p {...props}>{children}</p>;
    },
    // Render code content only. ReactMarkdown already wraps fenced code in <pre>.
    // Returning another <pre> here produced invalid nested markup (<pre><pre>...),
    // which Safari can re-parent and React then cannot reconcile reliably.
    code: ({ className, children, ...props }: {
      className?: string;
      children?: React.ReactNode;
    }) => (
      <code className={className} {...props}>{children}</code>
    ),
    pre: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement> & { children?: React.ReactNode }) => {
      const codeElement = React.Children.toArray(children).find(
        (child): child is React.ReactElement<{ className?: string; children?: React.ReactNode }> =>
          React.isValidElement<{ className?: string; children?: React.ReactNode }>(child),
      );
      const className = codeElement?.props.className;
      const codeContent = String(codeElement?.props.children ?? '').replace(/\n$/, '');
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';

      return (
        <pre {...props} style={{ position: 'relative' }}>
          {children}
          {language && (
            <button
              onClick={() => handleDownloadCode(codeContent, language)}
              className="code-download-button"
              title={`Download ${language.toUpperCase()} file`}
              aria-label={`Download ${language.toUpperCase()} file`}
            >
              ⬇️
            </button>
          )}
        </pre>
      );
    },
    img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => {
      // Check if this is a similarity badge URL
      if (src?.startsWith('/similarity-badge/')) {
        try {
          // Parse URL: /similarity-badge/{percentage}/{description}
          const parts = src.split('/').filter(Boolean);
          if (parts.length >= 2 && parts[0] === 'similarity-badge') {
            const percentage = parseFloat(parts[1]);

            if (!isNaN(percentage)) {
              return <SimilarityBadge percentage={percentage} />;
            }
          }
        } catch (error) {
          console.warn('Failed to parse similarity badge URL:', src, error);
        }
      }

      // Default: render as image
      return <ImageWithPlaceholder src={src} alt={alt} className="message-image" />;
    },
    table: ({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
      <div className="message-table-wrapper">
        <table {...props}>{children}</table>
      </div>
    ),
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      // Check if this is an internal entity link (relative or absolute)
      // Extract the path from absolute URLs or use href directly for relative URLs
      let entityPath = href;
      if (href?.includes('://')) {
        // For absolute URLs, extract just the path part
        try {
          const url = new URL(href);
          entityPath = url.pathname;
        } catch (error) {
          // If URL parsing fails, fall through to normal link handling
          console.error('Failed to parse absolute URL for entity path:', href, error);
          entityPath = undefined;
        }
      }

      if (entityPath?.startsWith('/items/')) {
        const match = entityPath.match(/^\/items\/(sets|parts|minifigs|elements)\/([^?]+)(\?.*)?$/);
        if (match) {
          const [, entityType, entityId, queryString] = match;
          // Extract query params from original href (which may include absolute URL params)
          let fullUrl = null;
          try {
            fullUrl = href?.includes('://') ? new URL(href!) : null;
          } catch (error) {
            console.error('Failed to parse absolute URL for query params:', href, error);
            fullUrl = null;
          }
          const params = fullUrl
            ? new URLSearchParams(fullUrl.search)
            : new URLSearchParams(queryString || '');

          return (
            <EntityHover
              type={entityType as 'sets' | 'parts' | 'minifigs' | 'elements'}
              id={entityId}
              colorId={params.get('color') || undefined}
              className="message-link"
              onSuggestionClick={onSuggestionClick}
              onClick={(e) => {
                // Allow Ctrl+Click, Cmd+Click, and middle-click to open in new tab
                if (e.ctrlKey || e.metaKey || e.button === 1) {
                  return; // Let the browser handle it
                }
                // For normal clicks, navigate to detail page with conversation context
                e.preventDefault();
                // Use the path for navigation, not the full absolute URL
                const pathForNavigation = entityPath + (queryString || '');
                const hrefWithConversation = conversationId
                  ? `${pathForNavigation}${pathForNavigation.includes('?') ? '&' : '?'}conversationId=${conversationId}`
                  : pathForNavigation;
                window.location.href = hrefWithConversation;
              }}
            >
              {children}
            </EntityHover>
          );
        }
      }

      // Check if this is a suggestion link
      if (href?.startsWith('#/suggestion/')) {
        try {
          const suggestion = decodeURIComponent(href.replace('#/suggestion/', ''));
          return (
            <a
              {...props}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                onSuggestionClick?.(suggestion);
              }}
              className="message-link suggestion-link"
          >
            {children}
          </a>
        );
        } catch (error) {
          console.error('Failed to decode suggestion URL:', error);
          // Fallback to regular link if decoding fails
        }
      }

      // Regular external link
      return (
        <a
          href={href}
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          className="message-link"
        >
          {children}
        </a>
      );
    }
  }), [handleDownloadCode, onSuggestionClick, conversationId]);

  // Feedback handlers
  const handleFeedbackClick = (type: FeedbackType) => {
    // Allow users to edit their existing feedback by clicking the active button
    if (feedbackType === type) {
      setPendingFeedbackType(type);
      setShowFeedbackDialog(true);
    } else {
      // First time feedback or changing feedback type
      setPendingFeedbackType(type);
      setShowFeedbackDialog(true);
    }
  };

  const handleFeedbackSubmit = async (submittedFeedbackText: string) => {
    if (!pendingFeedbackType || !conversationId) return;

    try {
      await chatApi.submitFeedback({
        conversationId,
        messageId: message.id,
        feedbackType: pendingFeedbackType,
        feedbackText: submittedFeedbackText || undefined,
      });
      setFeedbackType(pendingFeedbackType);
      setFeedbackText(submittedFeedbackText || undefined);
      setPendingFeedbackType(null);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Reset on error
      setPendingFeedbackType(null);
    }
  };

  const renderContent = () => {
    switch (message.type) {
      case 'user':
        return <div className="message-content">{message.content}</div>;

      case 'assistant': {
        // Buffer markdown content to prevent incomplete elements from rendering
        const bufferedContent = bufferMarkdownElements(message.content, isStreaming && !message.isComplete);

        return (
          <div className="message-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {bufferedContent}
            </ReactMarkdown>
            {isStreaming && !message.isComplete && (
              <div className="typing-indicator">
                <div className="loading-spinner-small"><span></span></div>
              </div>
            )}
          </div>
        );
      }

      case 'status':
        return <div className="message-content">{message.content}</div>;

      case 'tool-call':
        return <ToolCallMessage content={message.content} />;

      case 'error':
        return <div className="message-content">❌ {message.content}</div>;

      case 'system':
        // Render system messages like assistant messages but with plain text (no markdown)
        return (
          <div className="message-content">
            {message.content}
            {onReloadConversation && (
              <button
                className="reload-button"
                onClick={onReloadConversation}
                disabled={!isGenerationComplete}
              >
                {isGenerationComplete ? (
                  <>✓ Generation Complete - Reloading...</>
                ) : (
                  <>⏳ Waiting for generation to complete...</>
                )}
              </button>
            )}
          </div>
        );

      default:
        return <div className="message-content">{message.content}</div>;
    }
  };

  const getMessageClassName = () => {
    const baseClass = 'message';
    const typeClass = `message-${message.type}`;
    return `${baseClass} ${typeClass}`;
  };

  const getTimestamp = () => {
    return message.timestamp.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show feedback buttons for completed assistant messages
  const showFeedbackButtons =
    message.type === 'assistant' &&
    message.isComplete &&
    !isStreaming &&
    conversationId &&
    authService.isAuthenticated();

  return (
    <div className={getMessageClassName()}>
      {renderContent()}
      {message.type === 'user' && (
        <div className="message-timestamp">
          {getTimestamp()}
        </div>
      )}
      {showFeedbackButtons && (
        <>
          {!feedbackType && (
            <div className="message-feedback">
              <button
                className="feedback-button"
                onClick={() => handleFeedbackClick('POSITIVE')}
                aria-label="Good response"
                title="Good response"
              >
                👍
              </button>
              <button
                className="feedback-button"
                onClick={() => handleFeedbackClick('NEGATIVE')}
                aria-label="Bad response"
                title="Bad response"
              >
                👎
              </button>
            </div>
          )}
          {feedbackType && (
            <div className="feedback-text">
              <strong>{feedbackType === 'POSITIVE' ? '👍' : '👎'} {feedbackText ? 'Your feedback:' : 'Thank you for your feedback!'}</strong>
              {feedbackText && <span> {feedbackText}</span>}
            </div>
          )}
        </>
      )}
      {pendingFeedbackType && (
        <FeedbackDialog
          isOpen={showFeedbackDialog}
          feedbackType={pendingFeedbackType}
          onClose={() => {
            setShowFeedbackDialog(false);
            setPendingFeedbackType(null);
          }}
          onSubmit={handleFeedbackSubmit}
          initialFeedbackText={feedbackType === pendingFeedbackType ? feedbackText : ''}
        />
      )}
    </div>
  );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
